/**
 * BusinessLayout.tsx
 * 
 * This component provides the main layout structure for business dashboard pages.
 * It includes:
 * 
 * - Business-specific sidebar navigation with business routes
 * - Top header with business user actions and notifications
 * - Main content area with proper spacing
 * - Responsive design that adapts to mobile and desktop
 * - Business user authentication status display
 * - Navigation links to business-specific sections
 * 
 * The layout wraps business dashboard content and provides consistent
 * navigation and user interface elements across all business pages.
 */

import React, { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Home,
  MessageCircle,
  User,
  Settings,
  Menu,
  X,
  Search,
  Plus,
  ChevronDown,
  ArrowLeft,
  LogOut,
  Bell,
  CreditCard,
  Building2,
  Users,
  Shield,
  Database,
  Code,
  BarChart3,
  ShieldCheck,
  Bot
} from 'lucide-react'
import Button from './ui/Button'
import { useAuth } from '../contexts/AuthContext'

const BusinessLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state, logout } = useAuth()
  const user = state.user
  const location = useLocation()
  const navigate = useNavigate()
  const { orgId } = useParams<{ orgId: string }>()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const handleLogout = async () => {
    logout()
    // Force page refresh to update the homepage navigation state
    window.location.href = '/'
  }

  const businessNavigation = orgId ? [
    { name: 'Overview', href: `/orgs/${orgId}`, icon: Home },
    { name: 'Team', href: `/orgs/${orgId}/team`, icon: Users },
    { name: 'Chatbots', href: `/orgs/${orgId}/bots`, icon: MessageCircle },
    { name: 'AI Agent', href: `/orgs/${orgId}/ai-agent`, icon: Bot },
    { name: 'Analytics', href: `/orgs/${orgId}/analytics`, icon: BarChart3 },
    { name: 'Integrations', href: `/orgs/${orgId}/integrations`, icon: Code },
    { name: 'Audit Logs', href: `/orgs/${orgId}/audit`, icon: Shield },
    { name: 'Billing', href: `/orgs/${orgId}/billing`, icon: CreditCard },
    { name: 'Compliance', href: `/orgs/${orgId}/compliance`, icon: ShieldCheck },
    { name: 'Profile', href: `/orgs/${orgId}/profile`, icon: User },
    { name: 'Settings', href: `/orgs/${orgId}/settings`, icon: Settings },
  ] : []

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <Building2 className="h-8 w-8 text-indigo-500" />
            <div>
              <span className="text-lg font-bold text-gray-900">mobefai</span>
              <div className="text-xs text-indigo-600 font-medium">Business</div>
            </div>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Business user profile */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold">
                {user?.name?.charAt(0) || 'B'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'Business Admin'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email || 'business@demo.com'}
              </p>
              <div className="flex items-center mt-1">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-1"></div>
                <span className="text-xs text-indigo-600 font-medium">Enterprise</span>
              </div>
            </div>
          </div>
        </div>

        {/* Business Navigation */}
        <nav className="px-6 py-4 space-y-1 flex-1 overflow-y-auto">
          {businessNavigation.length > 0 ? (
            businessNavigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-indigo-50 text-indigo-700 border-r-2 border-indigo-500'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              )
            })
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-sm">Loading organization...</div>
            </div>
          )}
        </nav>

        {/* Logout button */}
        <div className="p-6 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col lg:ml-0">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {/* Breadcrumb */}
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Building2 className="h-4 w-4" />
                <span>Business Dashboard</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Notifications */}
              <button className="p-2 text-gray-400 hover:text-gray-600 relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-indigo-500 rounded-full"></span>
              </button>

              {/* Quick Actions */}
              <Button variant="primary" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                New Chatbot
              </Button>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {user?.name?.charAt(0) || 'B'}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <Link
                      to={orgId ? `/orgs/${orgId}/profile` : '#'}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Profile
                    </Link>
                    <Link
                      to={orgId ? `/orgs/${orgId}/settings` : '#'}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Settings
                    </Link>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}

export default BusinessLayout
